import type { ReactNode } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { formatDurationMs } from '@/shared/lib/format-duration';
import { Badge } from '@/shared/ui/shadcn/badge';
import { cn } from '@/shared/lib/utils';

export type RunFlowStatus = 'succeeded' | 'failed' | 'skipped' | 'running' | 'empty' | string;

export type RunFlowNode = {
  id: string;
  kind: 'step' | 'skill' | 'skill_step' | 'llm' | 'context';
  title: string;
  subtitle?: string;
  status?: RunFlowStatus;
  meta?: string;
  /** Wall time for this thinking stage (ms). Shown as a dedicated badge. */
  durationMs?: number | null;
  depth?: number;
  selected?: boolean;
  /** Shown inside the node card when selected/expanded. */
  expandedContent?: ReactNode;
};

type RunFlowProps = {
  nodes: RunFlowNode[];
  onSelect?: (id: string) => void;
  empty?: ReactNode;
  className?: string;
};

function statusVariant(
  status: RunFlowStatus | undefined,
): 'default' | 'secondary' | 'destructive' | 'outline' {
  if (status === 'succeeded') return 'secondary';
  if (status === 'failed') return 'destructive';
  if (status === 'running') return 'default';
  if (status === 'empty' || status === 'skipped' || status === 'not_reached') return 'outline';
  return 'outline';
}

export function RunFlow({ nodes, onSelect, empty, className }: RunFlowProps) {
  if (nodes.length === 0) {
    return (
      <div className={cn('text-sm text-muted-foreground', className)}>
        {empty ?? 'No run steps yet.'}
      </div>
    );
  }

  return (
    <ol className={cn('relative space-y-0', className)}>
      {nodes.map((node, index) => {
        const depth = node.depth ?? 0;
        const isLast = index === nodes.length - 1;
        const expanded = Boolean(node.selected && node.expandedContent);
        const showDuration = node.kind !== 'context';
        return (
          <li key={node.id} className="relative" style={{ paddingLeft: depth * 16 }}>
            {!isLast ? (
              <span
                className="absolute top-8 bottom-0 left-[11px] w-px bg-border"
                style={{ marginLeft: depth * 16 }}
                aria-hidden
              />
            ) : null}
            <div
              className={cn(
                'relative mb-2 overflow-hidden rounded-lg border bg-background transition-colors',
                node.selected ? 'border-primary ring-1 ring-primary/30' : 'border-border',
              )}
            >
              <button
                type="button"
                onClick={() => onSelect?.(node.id)}
                aria-expanded={expanded}
                className="flex w-full items-start gap-3 px-3 py-2.5 text-left hover:bg-muted/40"
              >
                <span
                  className={cn(
                    'mt-1.5 size-2.5 shrink-0 rounded-full',
                    node.status === 'failed'
                      ? 'bg-destructive'
                      : node.status === 'succeeded'
                        ? 'bg-emerald-500'
                        : node.status === 'running'
                          ? 'bg-primary animate-pulse'
                          : node.status === 'empty'
                            ? 'bg-amber-500'
                            : 'bg-muted-foreground/40',
                  )}
                  aria-hidden
                />
                <div className="min-w-0 flex-1 space-y-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-medium">{node.title}</span>
                    {node.status ? (
                      <Badge
                        variant={statusVariant(node.status)}
                        className="font-normal capitalize"
                      >
                        {node.status}
                      </Badge>
                    ) : null}
                    <Badge variant="outline" className="font-normal capitalize">
                      {node.kind.replace('_', ' ')}
                    </Badge>
                    {showDuration ? (
                      <Badge
                        variant="outline"
                        className="font-mono text-[10px] font-normal tabular-nums"
                        title="Execution time"
                      >
                        {formatDurationMs(node.durationMs)}
                      </Badge>
                    ) : null}
                  </div>
                  {node.subtitle ? (
                    <p className="text-xs text-muted-foreground">{node.subtitle}</p>
                  ) : null}
                  {node.meta ? (
                    <p className="text-[11px] text-muted-foreground">{node.meta}</p>
                  ) : null}
                </div>
                {node.expandedContent ? (
                  expanded ? (
                    <ChevronDown className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
                  ) : (
                    <ChevronRight className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
                  )
                ) : null}
              </button>
              {expanded ? (
                <div className="space-y-2 border-t border-border bg-muted/20 px-3 py-2.5">
                  <p className="text-[11px] font-medium tracking-wide text-muted-foreground uppercase">
                    Details
                  </p>
                  {node.expandedContent}
                </div>
              ) : null}
            </div>
          </li>
        );
      })}
    </ol>
  );
}
